import { useEffect, useMemo, useState } from 'react'
import { Alert, Autocomplete, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import _ from 'lodash'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import PageTitle from '../../components/PageTitle'
import PageBanner from '../AppointmentDeliveriesV2/shared/PageBanner'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import { merchantConfig, setWdAutoClosureDays, serverMessage } from './api'
import CategoryReportCard from './CategoryReportCard'
import AutoProposalCard from './AutoProposalCard'

export const PLATFORM_DEFAULT_DAYS = 7
export const MIN_DAYS = 1
export const MAX_DAYS = 15

/**
 * Per-merchant dispute TAT: how many days a merchant has to accept or dispute a
 * discrepancy before it auto-accepts (D7–D9). Backed by
 * POST /api/config/merchant/{id}/wdAutoClosureDays?days=1..15 (audited); no
 * days = clear the override. Only NEW discrepancies pick the new value up —
 * open ones keep the deadline they were raised with.
 */
export default function SettingsPage() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const options = useMemo(
    () => _.orderBy(merchants.filter((m) => m.is_deleted === '0' || m.is_deleted === undefined), 'name')
      .map((m) => ({ value: m.id, label: m.companyName ? `${m.name} (${m.companyName})` : m.name })),
    [merchants]
  )

  const [merchant, setMerchant] = useState(null)
  const [current, setCurrent] = useState(undefined)   // undefined = not loaded, null = platform default
  const [days, setDays] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!merchant) { setCurrent(undefined); setDays(''); return undefined }
    let alive = true
    setLoading(true)
    merchantConfig(merchant.value)
      .then((conf) => {
        if (!alive) return
        const v = conf && conf.wdAutoClosureDays != null ? Number(conf.wdAutoClosureDays) : null
        setCurrent(v)
        setDays(v == null ? '' : String(v))
      })
      .catch((err) => { if (alive) { setCurrent(null); enqueueSnackbar(serverMessage(err, 'Could not load the merchant config'), { variant: 'error' }) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [merchant, enqueueSnackbar])

  const n = Number(days)
  const valid = days !== '' && Number.isInteger(n) && n >= MIN_DAYS && n <= MAX_DAYS
  const unchanged = current !== undefined && ((current == null && days === '') || (current != null && String(current) === days))

  const save = async (value) => {
    if (!merchant) return
    setSaving(true)
    try {
      await setWdAutoClosureDays(merchant.value, value)
      setCurrent(value)
      setDays(value == null ? '' : String(value))
      enqueueSnackbar(value == null
        ? `${merchant.label}: back to the platform default (${PLATFORM_DEFAULT_DAYS} days)`
        : `${merchant.label}: ${value} day${value === 1 ? '' : 's'} to act`, { variant: 'success' })
    } catch (err) {
      enqueueSnackbar(serverMessage(err, 'Could not save'), { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <PageTitle>Weight v2 — Dispute Settings</PageTitle>
      <PageBanner variant='info' icon={<TimerOutlinedIcon />} title='How long a merchant has to act on a weight discrepancy'>
        The platform default is {PLATFORM_DEFAULT_DAYS} days. A merchant-specific value between {MIN_DAYS} and {MAX_DAYS} days
        overrides it for discrepancies raised <b>after</b> the change; open discrepancies keep the deadline they were raised with.
        The deadline is the time the discrepancy was raised plus the window (raised 22 Oct, 15:32 with 7 days is due 29 Oct, 15:32),
        and unanswered discrepancies auto-accept on the next sweep after it.
      </PageBanner>

      <Card sx={{ maxWidth: 720, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
        <CardContent>
          <Autocomplete
            options={options}
            value={merchant}
            onChange={(_e, v) => setMerchant(v)}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => <TextField {...params} label='Merchant' size='small' />}
            sx={{ mb: 2 }}
          />

          {merchant && (
            <Box>
              <Typography sx={{ fontSize: 12, color: '#4b5563', mb: 1.5 }}>
                {loading ? 'Loading the current setting…'
                  : current == null
                    ? <>Currently on the <b>platform default ({PLATFORM_DEFAULT_DAYS} days)</b>.</>
                    : <>Currently <b>{current} day{current === 1 ? '' : 's'}</b> (merchant-specific).</>}
              </Typography>
              <Stack direction='row' spacing={1} alignItems='flex-start' flexWrap='wrap' rowGap={1}>
                <TextField
                  label={`Days to act (${MIN_DAYS}–${MAX_DAYS})`}
                  type='number'
                  size='small'
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  inputProps={{ min: MIN_DAYS, max: MAX_DAYS, step: 1 }}
                  error={days !== '' && !valid}
                  helperText={days !== '' && !valid ? `Whole number between ${MIN_DAYS} and ${MAX_DAYS}` : ' '}
                  sx={{ width: 200 }}
                  disabled={loading}
                />
                <LoadingButton variant='contained' loading={saving} disabled={!valid || unchanged || loading} onClick={() => save(n)} sx={{ fontSize: 12, fontWeight: 700 }}>
                  Save
                </LoadingButton>
                <Button variant='outlined' disabled={saving || loading || current == null} onClick={() => save(null)} sx={{ fontSize: 12 }}>
                  Use platform default
                </Button>
              </Stack>
              <Alert severity='info' sx={{ fontSize: 12, mt: 1 }}>
                Every change is audited on the merchant config (who, when, from → to).
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Item 6 (category rule) and T12/T13 (auto-proposal engine) live on the same merchant picker:
          both are per-merchant weight settings, which is this page's remit (Item 7 scope). */}
      <CategoryReportCard merchant={merchant} />
      <AutoProposalCard merchant={merchant} />
    </>
  )
}
